import type { AiBlock, AiEnvelope } from '../../api/client';

/**
 * Ось «проверено» в ответах Аналитика не показывается (журнал №63): проверка
 * живёт только в поручениях и в очереди проверки, а не в метрике сезона.
 *
 * Сервер (инструмент `M-WORK-reported`) пока отдаёт блок `done_split` с двумя
 * осями — «отчитались» и «проверено». Правка рецепта — на стороне 6al.2; до неё
 * экран убирает этот блок из показа. Числа «отчитались» и «без отчёта»
 * остаются в карточках KPI того же ответа, поэтому ответ не теряет вывода.
 * Сам конверт (следы проверки, происхождение) не меняется: убирается только
 * блок отрисовки.
 */
export function withoutVerifiedAxis(envelope: AiEnvelope): AiEnvelope {
  if (envelope.kind !== 'answer') return envelope;
  const ok = envelope;
  const spec = ok.spec;
  const keep = (blocks: AiBlock[]) => blocks.filter((block) => block.type !== 'done_split');
  const blocks = keep(spec.blocks ?? []);
  const disclosure = keep(spec.disclosure ?? []);
  if (blocks.length === (spec.blocks ?? []).length && disclosure.length === (spec.disclosure ?? []).length) {
    return envelope;
  }
  return { ...ok, spec: { ...spec, blocks, disclosure } };
}

/**
 * Доля рецепта прячется, когда рядом показана доля ТЕКУЩЕГО цикла (6al.13).
 *
 * Инструмент `M-WORK-reported` считает по всем открытым на дату кампаниям
 * района, а панель цикла — по обязательствам цикла (`season/picture`, как в
 * «Обзоре района»). Две доли одного вопроса на одном экране («72,5 %» и
 * «203 из 1 145») читаются как противоречие. Передать цикл в инструмент —
 * новый параметр контракта реестра (схема плана, хэш, версия определения);
 * до этой правки экран оставляет одну долю — цикловую — и убирает из ответа
 * вывод и карточки KPI. Таблица «каких кампаний ещё ждут» остаётся: она
 * отвечает на другой вопрос и подписана как срез открытых кампаний.
 */
export function withoutShareBlocks(envelope: AiEnvelope): AiEnvelope {
  if (envelope.kind !== 'answer') return envelope;
  const spec = envelope.spec;
  const share = new Set(['verdict', 'kpi_row', 'done_split']);
  const keep = (blocks: AiBlock[]) => blocks.filter((block) => !share.has(block.type));
  return {
    ...envelope,
    spec: { ...spec, blocks: keep(spec.blocks ?? []), disclosure: keep(spec.disclosure ?? []) },
  };
}

/**
 * Доля текущего цикла для готового вопроса «Как идут сезонные работы?»
 * (agroai-6al.12) — из СЛОЯ МЕТРИК (`GET /ai/metrics`), а не из рецепта.
 *
 * Почему не параметр цикла в `M-WORK-reported`: популяция рецепта — открытые
 * на дату кампании, и цикл в нём менял бы смысл записи реестра (новая версия
 * инструмента ради числа, которое слой метрик уже считает). Слой метрик —
 * тот же исполнитель `query_metrics`, что у ассистента и у тура: одно
 * определение с номером версии, тот же срез, что у KPI «Выполнено» Обзора
 * района (`reports.done_pct`). Прежняя панель брала «отчитались» из картины
 * сезона и после 6al.35 расходилась с Обзором, где главная доля — «выполнено».
 */
export const CYCLE_SHARE_METRICS = ['obligations.total', 'reports.done', 'reports.in_progress', 'reports.done_pct'] as const;

export interface MetricRow {
  id: string;
  ref?: string;
  value: unknown;
  error?: string | null;
}

export interface CycleShareCounts {
  total: number;
  done: number;
  inProgress: number;
  /** Доля «выполнено», как её назвал слой; `null` — не посчитана. */
  donePct: number | null;
  /** `metric@version` использованных определений — для подписи. */
  refs: string[];
}

/** Значения слоя → счётчики панели. Нет любого из трёх чисел — панели нет: половина доли хуже никакой. */
export function cycleShareFrom(rows: readonly MetricRow[] | null | undefined): CycleShareCounts | null {
  if (!rows) return null;
  const by = new Map(rows.map((row) => [row.id, row]));
  const number = (id: string): number | null => {
    const row = by.get(id);
    return row && !row.error && typeof row.value === 'number' && Number.isFinite(row.value) ? row.value : null;
  };
  const total = number('obligations.total');
  const done = number('reports.done');
  const inProgress = number('reports.in_progress');
  if (total === null || done === null || inProgress === null) return null;
  return {
    total,
    done,
    inProgress,
    donePct: number('reports.done_pct'),
    refs: CYCLE_SHARE_METRICS.map((id) => by.get(id)?.ref).filter((ref): ref is string => !!ref),
  };
}
